/**
 * Settings: units/theme/voice/camera toggles, route avoidances, vehicle
 * profiles with editable truck dimensions, and (admins only) the user-approval
 * section. Renders from the store; every control writes straight back to it.
 */

import { store } from '../core/store';
import { newVehicle, type Settings, type VehicleKind, type VehicleProfile } from '../core/settings';
import * as api from '../auth/api';
import type { PendingUser, User } from '../auth/api';
import { t } from '../core/i18n';

export class SettingsPanel {
  readonly el: HTMLElement;
  private adminUsers: PendingUser[] | null = null;

  constructor(
    parent: HTMLElement,
    private currentUser: () => User | null,
    private onClose: () => void,
    private onLogout: () => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'panel panel--settings';
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  async open(): Promise<void> {
    this.el.hidden = false;
    this.render();
    if (this.currentUser()?.role === 'admin') {
      try {
        this.adminUsers = (await api.adminListUsers()).users;
      } catch {
        this.adminUsers = null;
      }
      if (!this.el.hidden) this.render();
    }
  }

  close(): void {
    this.el.hidden = true;
    this.adminUsers = null;
  }

  private render(): void {
    const s = store.get().settings;
    const user = this.currentUser();

    this.el.innerHTML = `
      <div class="panel__bar">
        <button class="icon-btn" data-act="close" aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="M15 4 L7 12 L15 20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <h2>${t('set.title')}</h2>
      </div>
      <div class="panel__scroll">

        <section class="set-group">
          <h3>${t('set.vehicle')}</h3>
          <div class="vehicle-cards">${s.vehicles.map((v) => this.vehicleCard(v, s)).join('')}</div>
          <div class="vehicle-add">
            <button class="btn btn--small btn--ghost" data-addvehicle="car">${t('set.vehicle.addCar')}</button>
            <button class="btn btn--small btn--ghost" data-addvehicle="truck">${t('set.vehicle.addTruck')}</button>
          </div>
        </section>

        <section class="set-group">
          <h3>${t('set.pace')}</h3>
          <div class="panel__hint panel__hint--tight">${t('set.pace.hint')}</div>
          ${this.speed('motorway', t('set.pace.motorway'), s.speeds.motorway)}
          ${this.speed('dual', t('set.pace.dual'), s.speeds.dual)}
          ${this.speed('single', t('set.pace.single'), s.speeds.single)}
          <label class="set-row">
            <span>${t('set.pace.signal')}</span>
            <select data-set="signalSeconds">
              ${[0, 30, 60, 90, 120, 150, 180, 240]
                .map(
                  (v) =>
                    `<option value="${v}" ${s.signalSeconds === v ? 'selected' : ''}>${
                      v === 0 ? t('set.overspeed.off') : `${v < 60 ? `${v} s` : `${v / 60} min`}`
                    }</option>`,
                )
                .join('')}
            </select>
          </label>
        </section>

        <section class="set-group">
          <h3>${t('set.route')}</h3>
          ${this.toggle('route.avoidTolls', t('preview.avoidTolls'), s.route.avoidTolls)}
          ${this.toggle('route.avoidMotorways', t('preview.avoidMotorways'), s.route.avoidMotorways)}
          ${this.toggle('route.avoidFerries', t('set.avoidFerries'), s.route.avoidFerries)}
          ${this.toggle('route.avoidUnpaved', t('set.avoidUnpaved'), s.route.avoidUnpaved)}
          ${this.toggle('route.useTraffic', t('set.useTraffic'), s.route.useTraffic)}
          ${this.toggle('route.alternatives', t('set.alternatives'), s.route.alternatives)}
          <label class="set-row">
            <span>${t('set.overlay')}</span>
            <select data-set="drivenOverlay">
              <option value="off" ${s.drivenOverlay === 'off' ? 'selected' : ''}>${t('set.overlay.off')}</option>
              <option value="speed" ${s.drivenOverlay === 'speed' ? 'selected' : ''}>${t('set.overlay.speed')}</option>
              <option value="rough" ${s.drivenOverlay === 'rough' ? 'selected' : ''}>${t('set.overlay.rough')}</option>
            </select>
          </label>
        </section>

        <section class="set-group">
          <h3>${t('set.guidance')}</h3>
          ${this.toggle('voiceEnabled', t('set.voice'), s.voiceEnabled)}
          ${this.toggle('duckMusic', t('set.duck'), s.duckMusic)}
          ${this.toggle('adaptiveCamera', t('set.autozoom'), s.adaptiveCamera)}
          ${this.toggle('keepAwake', t('set.awake'), s.keepAwake)}
          <label class="set-row">
            <span>${t('set.overspeed')}</span>
            <select data-set="speedWarnOffset">
              ${[0, 5, 10, 15, 20]
                .map(
                  (v) =>
                    `<option value="${v}" ${s.speedWarnOffset === v ? 'selected' : ''}>${v === 0 ? t('set.overspeed.off') : `+${v} km/h`}</option>`,
                )
                .join('')}
            </select>
          </label>
        </section>

        <section class="set-group">
          <h3>${t('set.display')}</h3>
          <label class="set-row">
            <span>${t('set.language')}</span>
            <select data-set="language">
              <option value="en" ${s.language === 'en' ? 'selected' : ''}>English</option>
              <option value="th" ${s.language === 'th' ? 'selected' : ''}>ไทย</option>
            </select>
          </label>
          <label class="set-row">
            <span>${t('set.theme')}</span>
            <select data-set="theme">
              <option value="auto" ${s.theme === 'auto' ? 'selected' : ''}>${t('set.theme.auto')}</option>
              <option value="night" ${s.theme === 'night' ? 'selected' : ''}>${t('set.theme.night')}</option>
              <option value="day" ${s.theme === 'day' ? 'selected' : ''}>${t('set.theme.day')}</option>
            </select>
          </label>
          <label class="set-row">
            <span>${t('set.units')}</span>
            <select data-set="units">
              <option value="metric" ${s.units === 'metric' ? 'selected' : ''}>${t('set.units.km')}</option>
              <option value="imperial" ${s.units === 'imperial' ? 'selected' : ''}>${t('set.units.mi')}</option>
            </select>
          </label>
          ${this.toggle('offlineTiles', t('set.offline'), s.offlineTiles)}
          ${this.toggle('logTrips', t('set.logTrips'), s.logTrips)}
        </section>

        ${user?.role === 'admin' ? this.adminSection() : ''}

        <section class="set-group">
          <h3>${t('set.account')}</h3>
          <div class="set-row"><span>${t('set.signedInAs')}</span><strong>${escapeHtml(user?.name ?? '—')}${user?.role === 'admin' ? t('set.admin.tag') : ''}</strong></div>
          <button class="btn btn--ghost" data-act="logout">${t('set.signOut')}</button>
        </section>
      </div>`;

    this.bind();
  }

  /**
   * Every profile is editable now, not just the truck's dimensions - drivers
   * add their own vehicles, so the name and the fuel figure have to be theirs
   * too. Truck dimensions still drive the routing constraints (`max_height`
   * and friends), which is why they stay prominent on a truck profile.
   */
  private vehicleCard(v: VehicleProfile, s: Settings): string {
    const active = v.id === s.activeVehicleId;
    const removable = s.vehicles.length > 1;
    return `
      <div class="vehicle-card ${active ? 'is-active' : ''}" data-vehicle="${v.id}">
        <button class="vehicle-card__pick">
          <strong>${escapeHtml(v.name)}</strong>
          <span>${v.kind === 'truck' ? t('set.vehicle.truck') : t('set.vehicle.car')}</span>
        </button>
        <button class="vehicle-card__edit" data-editvehicle="${v.id}" aria-label="${t('set.vehicle.edit')}">
          <svg viewBox="0 0 24 24"><path d="M4 20 h4 L19 9 a2.1 2.1 0 0 0 -3 -3 L5 17 Z M14 7 l3 3" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round" stroke-linecap="round"/></svg>
        </button>
        <div class="vehicle-card__editor" data-editor="${v.id}" hidden>
          <label class="vehicle-field">${t('set.vehicle.name')}
            <input type="text" maxlength="40" data-vname="${v.id}" value="${escapeAttr(v.name)}"/>
          </label>
          <label class="vehicle-field">${t('set.vehicle.kind')}
            <select data-vkind="${v.id}">
              <option value="car" ${v.kind === 'car' ? 'selected' : ''}>${t('set.vehicle.car')}</option>
              <option value="truck" ${v.kind === 'truck' ? 'selected' : ''}>${t('set.vehicle.truck')}</option>
            </select>
          </label>
          <label class="vehicle-field vehicle-field--switch">
            <span>${t('set.easyPass')}</span>
            <span class="switch"><input type="checkbox" data-veasypass="${v.id}" ${v.easyPass ? 'checked' : ''}/><span class="switch__knob"></span></span>
          </label>
          <div class="vehicle-card__dims">
            ${this.dim(v.id, 'height', 'H (m)', v.height)}
            ${this.dim(v.id, 'width', 'W (m)', v.width)}
            ${this.dim(v.id, 'weight', 'Wt (t)', v.weight)}
            ${this.dim(v.id, 'consumption', 'L/100km', v.consumption)}
          </div>
          ${
            removable
              ? `<button class="btn btn--small vehicle-card__delete" data-delvehicle="${v.id}">${t('set.vehicle.delete')}</button>`
              : ''
          }
        </div>
      </div>`;
  }

  private dim(
    vid: string,
    key: 'height' | 'width' | 'weight' | 'consumption',
    label: string,
    value?: number,
  ): string {
    return `<label>${label}<input type="number" step="0.1" min="0" inputmode="decimal"
      data-dim="${vid}:${key}" value="${value ?? ''}"/></label>`;
  }

  /** A cruising-speed row. These drive every ETA the app shows. */
  private speed(key: keyof Settings['speeds'], label: string, value: number): string {
    return `
      <label class="set-row">
        <span>${label}</span>
        <span class="speed-input">
          <input type="number" min="10" max="160" step="5" inputmode="numeric"
                 data-speed="${key}" value="${value}"/>
          <em>km/h</em>
        </span>
      </label>`;
  }

  private toggle(path: string, label: string, on: boolean): string {
    return `
      <label class="set-row">
        <span>${label}</span>
        <span class="switch"><input type="checkbox" data-toggle="${path}" ${on ? 'checked' : ''}/><span class="switch__knob"></span></span>
      </label>`;
  }

  private adminSection(): string {
    const users = this.adminUsers;
    const rows =
      users === null
        ? `<div class="panel__hint">${t('set.admin.loading')}</div>`
        : users.length === 0
          ? `<div class="panel__hint">${t('set.admin.none')}</div>`
          : users
              .map(
                (u) => `
        <div class="admin-user" data-uid="${u.id}">
          <span class="admin-user__name">${escapeHtml(u.name)}
            <em class="admin-user__status admin-user__status--${u.status}">${t(`set.status.${u.status}`)}</em>
          </span>
          <span class="admin-user__actions">
            ${
              u.status === 'pending'
                ? `<button class="btn btn--small btn--primary" data-approve="${u.id}">${t('set.admin.approve')}</button>`
                : ''
            }
            ${
              u.status !== 'disabled'
                ? `<button class="btn btn--small btn--ghost" data-disable="${u.id}">${t('set.admin.disable')}</button>`
                : `<button class="btn btn--small btn--ghost" data-approve="${u.id}">${t('set.admin.enable')}</button>`
            }
            <button class="btn btn--small btn--ghost" data-resetpin="${u.id}">${t('set.admin.resetPin')}</button>
          </span>
        </div>`,
              )
              .join('');
    return `<section class="set-group set-group--admin"><h3>${t('set.admin')}</h3>${rows}</section>`;
  }

  private bind(): void {
    this.el.querySelector('[data-act=close]')!.addEventListener('click', () => this.onClose());
    this.el.querySelector('[data-act=logout]')!.addEventListener('click', () => this.onLogout());

    this.el.querySelectorAll<HTMLInputElement>('[data-toggle]').forEach((input) => {
      input.addEventListener('change', () => {
        const path = input.dataset.toggle!;
        if (path.startsWith('route.')) {
          store.patchSettings({ route: { ...store.get().settings.route, [path.slice(6)]: input.checked } });
        } else {
          store.patchSettings({ [path]: input.checked } as Partial<Settings>);
        }
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-speed]').forEach((input) => {
      input.addEventListener('change', () => {
        const key = input.dataset.speed as keyof Settings['speeds'];
        const n = Number(input.value);
        // Clamp rather than reject: a stray keystroke must not produce a NaN
        // that silently poisons every ETA in the app.
        const value = Number.isFinite(n) ? Math.min(160, Math.max(10, Math.round(n))) : 60;
        input.value = String(value);
        store.patchSettings({ speeds: { ...store.get().settings.speeds, [key]: value } });
      });
    });

    this.el.querySelectorAll<HTMLSelectElement>('[data-set]').forEach((sel) => {
      sel.addEventListener('change', () => {
        const key = sel.dataset.set as keyof Settings;
        const value =
          key === 'speedWarnOffset' || key === 'signalSeconds' ? Number(sel.value) : sel.value;
        store.patchSettings({ [key]: value } as Partial<Settings>);
        // Language touches every rendered string - a clean reload is the
        // simplest correct way to apply it everywhere at once.
        if (key === 'language') location.reload();
      });
    });

    this.el.querySelectorAll<HTMLElement>('.vehicle-card__pick').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn.closest('.vehicle-card') as HTMLElement).dataset.vehicle!;
        store.patchSettings({ activeVehicleId: id });
        this.render();
      });
    });

    /* ---------------------------- vehicle CRUD --------------------------- */

    this.el.querySelectorAll<HTMLButtonElement>('[data-editvehicle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.editvehicle!;
        const editor = this.el.querySelector<HTMLElement>(`[data-editor="${cssEscape(id)}"]`);
        if (editor) editor.hidden = !editor.hidden;
        btn.classList.toggle('is-open', editor ? !editor.hidden : false);
      });
    });

    this.el.querySelectorAll<HTMLButtonElement>('[data-addvehicle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const v = newVehicle(btn.dataset.addvehicle as VehicleKind);
        const vehicles = [...store.get().settings.vehicles, v];
        store.patchSettings({ vehicles, activeVehicleId: v.id });
        this.render();
        // Open the new profile straight away - it needs a name.
        const editor = this.el.querySelector<HTMLElement>(`[data-editor="${cssEscape(v.id)}"]`);
        if (editor) editor.hidden = false;
        this.el.querySelector<HTMLInputElement>(`[data-vname="${cssEscape(v.id)}"]`)?.focus();
      });
    });

    this.el.querySelectorAll<HTMLButtonElement>('[data-delvehicle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.delvehicle!;
        const s = store.get().settings;
        if (s.vehicles.length <= 1) return; // never orphan the picker
        const vehicles = s.vehicles.filter((v) => v.id !== id);
        const activeVehicleId = s.activeVehicleId === id ? vehicles[0].id : s.activeVehicleId;
        store.patchSettings({ vehicles, activeVehicleId });
        this.render();
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-vname]').forEach((input) => {
      input.addEventListener('change', () => {
        const id = input.dataset.vname!;
        const name = input.value.trim() || t('set.vehicle.unnamed');
        const vehicles = store.get().settings.vehicles.map((v) =>
          v.id === id ? { ...v, name } : v,
        );
        store.patchSettings({ vehicles });
        this.render();
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-veasypass]').forEach((input) => {
      input.addEventListener('change', () => {
        const id = input.dataset.veasypass!;
        const vehicles = store.get().settings.vehicles.map((v) =>
          v.id === id ? { ...v, easyPass: input.checked } : v,
        );
        store.patchSettings({ vehicles });
      });
    });

    this.el.querySelectorAll<HTMLSelectElement>('[data-vkind]').forEach((sel) => {
      sel.addEventListener('change', () => {
        const id = sel.dataset.vkind!;
        const kind = sel.value as VehicleKind;
        const vehicles = store.get().settings.vehicles.map((v) =>
          v.id === id ? { ...v, kind } : v,
        );
        store.patchSettings({ vehicles });
        this.render();
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-dim]').forEach((input) => {
      input.addEventListener('change', () => {
        const [vid, key] = input.dataset.dim!.split(':') as [
          string,
          'height' | 'width' | 'weight' | 'consumption',
        ];
        const value = input.value === '' ? undefined : Number(input.value);
        const vehicles = store.get().settings.vehicles.map((v) =>
          v.id === vid ? { ...v, [key]: value } : v,
        );
        store.patchSettings({ vehicles });
      });
    });

    /* admin actions */
    this.el.querySelectorAll<HTMLButtonElement>('[data-approve]').forEach((b) =>
      b.addEventListener('click', () => void this.setStatus(Number(b.dataset.approve), 'approved')),
    );
    this.el.querySelectorAll<HTMLButtonElement>('[data-disable]').forEach((b) =>
      b.addEventListener('click', () => void this.setStatus(Number(b.dataset.disable), 'disabled')),
    );
    this.el.querySelectorAll<HTMLButtonElement>('[data-resetpin]').forEach((b) =>
      b.addEventListener('click', () => {
        const pin = prompt(t('set.admin.newPin'));
        if (!pin || !/^\d{4,10}$/.test(pin)) return;
        void api
          .adminResetPin(Number(b.dataset.resetpin), pin)
          .then(() => this.refreshAdmin())
          .catch((e) => alert(e.message));
      }),
    );
  }

  private async setStatus(userId: number, status: 'approved' | 'disabled'): Promise<void> {
    try {
      await api.adminSetStatus(userId, status);
      await this.refreshAdmin();
    } catch (e) {
      alert(e instanceof Error ? e.message : t('set.failed'));
    }
  }

  private async refreshAdmin(): Promise<void> {
    this.adminUsers = (await api.adminListUsers()).users;
    this.render();
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/** Vehicle ids are generated, but a selector still must not break on them. */
function cssEscape(s: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s) : s.replace(/["\\]/g, '\\$&');
}
